/**
 * TanStack Query keys and hooks.
 *
 * Two rules keep the cache honest:
 *
 *  1. **Every key starts with its resource name and is built by `queryKeys`.**
 *     Nothing writes a key array inline, so `invalidateQueries({ queryKey:
 *     queryKeys.tasks.all })` reliably catches every list and every filter.
 *  2. **A mutation invalidates the resources it could have changed, and says
 *     which in a comment when it is not obvious.** Moving a task changes the
 *     board, the task list, its feature's rollup and capacity - four caches
 *     from one call.
 *
 * Nothing here reads the session. Screens pass what they are filtering by, so
 * this module stays a thin, testable shell over `lib/api.ts` and there is no
 * import cycle with `lib/session.tsx`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import type {
  ApplicationDto,
  BoardDto,
  BoardQuery,
  CapacityQuery,
  CapacityReport,
  ClientDashboardDto,
  ClientDto,
  FeatureDto,
  MoveTaskInput,
  ProjectDto,
  TaskDto,
  TimeEntryDto,
  TimesheetQuery,
  UserDto,
} from '@botguide/shared';

import {
  applicationsApi,
  boardApi,
  capacityApi,
  clientsApi,
  dashboardApi,
  featuresApi,
  projectsApi,
  tasksApi,
  timeEntriesApi,
  usersApi,
  type PayRateDto,
} from './api';

/* -------------------------------------------------------------------------- */
/* Keys                                                                       */
/* -------------------------------------------------------------------------- */

export const queryKeys = {
  session: ['session'] as const,

  clients: {
    all: ['clients'] as const,
    list: () => ['clients', 'list'] as const,
    detail: (id: string) => ['clients', 'detail', id] as const,
  },
  applications: {
    all: ['applications'] as const,
    list: (params: { clientId?: string }) => ['applications', 'list', params] as const,
    detail: (id: string) => ['applications', 'detail', id] as const,
  },
  projects: {
    all: ['projects'] as const,
    list: (params: { clientId?: string; applicationId?: string }) =>
      ['projects', 'list', params] as const,
    detail: (id: string) => ['projects', 'detail', id] as const,
  },
  features: {
    all: ['features'] as const,
    list: (params: { projectId?: string; clientId?: string }) => ['features', 'list', params] as const,
    detail: (id: string) => ['features', 'detail', id] as const,
  },
  tasks: {
    all: ['tasks'] as const,
    list: (params: BoardQuery) => ['tasks', 'list', params] as const,
    detail: (id: string) => ['tasks', 'detail', id] as const,
  },
  board: {
    all: ['board'] as const,
    view: (params: BoardQuery) => ['board', 'view', params] as const,
  },
  users: {
    all: ['users'] as const,
    list: () => ['users', 'list'] as const,
    me: () => ['users', 'me'] as const,
    payRates: (id: string) => ['users', 'pay-rates', id] as const,
  },
  timeEntries: {
    all: ['time-entries'] as const,
    list: (params: TimesheetQuery) => ['time-entries', 'list', params] as const,
  },
  capacity: {
    all: ['capacity'] as const,
    report: (params: Partial<CapacityQuery>) => ['capacity', 'report', params] as const,
  },
  dashboard: {
    all: ['client-dashboard'] as const,
    /** `null` is the caller's own client; a string is an owner preview. */
    view: (clientId: string | null) => ['client-dashboard', 'view', clientId] as const,
  },
} as const;

/* -------------------------------------------------------------------------- */
/* Reads                                                                      */
/* -------------------------------------------------------------------------- */

export function useClients(): UseQueryResult<ClientDto[]> {
  return useQuery({ queryKey: queryKeys.clients.list(), queryFn: clientsApi.list });
}

export function useClient(id: string | undefined): UseQueryResult<ClientDto> {
  return useQuery({
    queryKey: queryKeys.clients.detail(id ?? ''),
    queryFn: () => clientsApi.get(id as string),
    enabled: Boolean(id),
  });
}

export function useApplications(params: { clientId?: string } = {}): UseQueryResult<ApplicationDto[]> {
  return useQuery({
    queryKey: queryKeys.applications.list(params),
    queryFn: () => applicationsApi.list(params),
  });
}

export function useProjects(
  params: { clientId?: string; applicationId?: string } = {},
): UseQueryResult<ProjectDto[]> {
  return useQuery({
    queryKey: queryKeys.projects.list(params),
    queryFn: () => projectsApi.list(params),
  });
}

/** Features arrive with their `rollup` already derived by the API. */
export function useFeatures(
  params: { projectId?: string; clientId?: string } = {},
): UseQueryResult<FeatureDto[]> {
  return useQuery({
    queryKey: queryKeys.features.list(params),
    queryFn: () => featuresApi.list(params),
  });
}

export function useTasks(params: BoardQuery = {}): UseQueryResult<TaskDto[]> {
  return useQuery({ queryKey: queryKeys.tasks.list(params), queryFn: () => tasksApi.list(params) });
}

export function useTask(id: string | undefined): UseQueryResult<TaskDto> {
  return useQuery({
    queryKey: queryKeys.tasks.detail(id ?? ''),
    queryFn: () => tasksApi.get(id as string),
    enabled: Boolean(id),
  });
}

/** The grouped board. Columns come from the API so both sides agree on order. */
export function useBoard(params: BoardQuery = {}): UseQueryResult<BoardDto> {
  return useQuery({ queryKey: queryKeys.board.view(params), queryFn: () => boardApi.get(params) });
}

export function useUsers(): UseQueryResult<UserDto[]> {
  return useQuery({ queryKey: queryKeys.users.list(), queryFn: usersApi.list });
}

export function useMe(): UseQueryResult<UserDto> {
  return useQuery({ queryKey: queryKeys.users.me(), queryFn: usersApi.me });
}

export function useTimeEntries(params: TimesheetQuery = {}): UseQueryResult<TimeEntryDto[]> {
  return useQuery({
    queryKey: queryKeys.timeEntries.list(params),
    queryFn: () => timeEntriesApi.list(params),
  });
}

export function useCapacity(params: Partial<CapacityQuery> = {}): UseQueryResult<CapacityReport> {
  return useQuery({
    queryKey: queryKeys.capacity.report(params),
    queryFn: () => capacityApi.get(params),
  });
}

/**
 * Pass a `clientId` only when an owner is previewing someone else's view.
 * Omit it and the API resolves the caller's own client.
 */
export function useClientDashboard(clientId?: string): UseQueryResult<ClientDashboardDto> {
  return useQuery({
    queryKey: queryKeys.dashboard.view(clientId ?? null),
    queryFn: () => (clientId ? dashboardApi.forClient(clientId) : dashboardApi.mine()),
  });
}

/* -------------------------------------------------------------------------- */
/* Writes                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * A task write can change its lane, its points, its assignee or its week, and
 * each of those feeds a different screen. Rather than reason about which, we
 * invalidate the four caches derived from tasks. They are cheap reads and a
 * stale board is a support ticket.
 */
function useTaskWriteInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    for (const key of [
      queryKeys.tasks.all,
      queryKeys.board.all,
      queryKeys.features.all,
      queryKeys.capacity.all,
      queryKeys.dashboard.all,
    ]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };
}

export function useMoveTask(): UseMutationResult<TaskDto, Error, { id: string; move: MoveTaskInput }> {
  const invalidate = useTaskWriteInvalidation();
  return useMutation({
    mutationFn: ({ id, move }) => tasksApi.move(id, move),
    onSuccess: invalidate,
  });
}

export function useUpdateTask(): UseMutationResult<
  TaskDto,
  Error,
  { id: string; patch: Parameters<typeof tasksApi.update>[1] }
> {
  const invalidate = useTaskWriteInvalidation();
  return useMutation({
    mutationFn: ({ id, patch }) => tasksApi.update(id, patch),
    onSuccess: invalidate,
  });
}

export function useCreateTask(): UseMutationResult<TaskDto, Error, Parameters<typeof tasksApi.create>[0]> {
  const invalidate = useTaskWriteInvalidation();
  return useMutation({ mutationFn: tasksApi.create, onSuccess: invalidate });
}

export function useDeleteTask(): UseMutationResult<void, Error, string> {
  const invalidate = useTaskWriteInvalidation();
  return useMutation({ mutationFn: tasksApi.remove, onSuccess: invalidate });
}

export function useCreateTimeEntry(): UseMutationResult<
  TimeEntryDto,
  Error,
  Parameters<typeof timeEntriesApi.create>[0]
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: timeEntriesApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
    },
  });
}

export function useUpdateTimeEntry(): UseMutationResult<
  TimeEntryDto,
  Error,
  { id: string; patch: Parameters<typeof timeEntriesApi.update>[1] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => timeEntriesApi.update(id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
    },
  });
}

export function useDeleteTimeEntry(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: timeEntriesApi.remove,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
    },
  });
}

/**
 * Approving hours is the only write that can change client spend, so it takes
 * the dashboard with it.
 */
export function useReviewTimeEntry(): UseMutationResult<
  TimeEntryDto,
  Error,
  { id: string; review: Parameters<typeof timeEntriesApi.review>[1] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, review }) => timeEntriesApi.review(id, review),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useUpdateUser(): UseMutationResult<
  UserDto,
  Error,
  { id: string; patch: Parameters<typeof usersApi.update>[1] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => usersApi.update(id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity.all });
    },
  });
}

export function useCreateUser(): UseMutationResult<UserDto, Error, Parameters<typeof usersApi.create>[0]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.capacity.all });
    },
  });
}

/** One person's pay history. Only fetched when a screen actually opens it. */
export function usePayRates(userId: string | null): UseQueryResult<PayRateDto[]> {
  return useQuery({
    queryKey: queryKeys.users.payRates(userId ?? ''),
    queryFn: () => usersApi.payRates(userId!),
    enabled: userId !== null,
  });
}

/**
 * Give somebody a rate from a date.
 *
 * Hours are priced at read time from the rate in force on the day worked, so
 * adding a rate can change what an already-approved entry earned. The
 * timesheet and the review screen are therefore invalidated too - otherwise a
 * cached "—" sits there looking like the rate did not take.
 */
export function useAddPayRate(
  userId: string,
): UseMutationResult<PayRateDto, Error, Parameters<typeof usersApi.addPayRate>[1]> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof usersApi.addPayRate>[1]) => usersApi.addPayRate(userId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all });
    },
  });
}
